import { I18NextPipe } from 'angular-i18next';
import { isNumber } from 'lodash';
import { MonitoringPlanType } from 'mbs-ui-shared@models/MonitoringPlanType';
import { BackupStatuses } from 'mbs-ui-shared@models/backupHistory';
import { LastPlanStatusDisplay } from 'mbs-ui-shared@models/last-plan-status';

export class MonitoringPlanTypeUtil {
  private static readonly defaultIcon = 'ico ico-file';

  private static readonly monitoringPlanTypeIconMap: Map<MonitoringPlanType, string> = new Map([
    [MonitoringPlanType.NA, 'ico ico-question-circle'],
    [MonitoringPlanType.Backup, this.defaultIcon],
    [MonitoringPlanType.Restore, this.defaultIcon],
    [MonitoringPlanType.BackupFiles, this.defaultIcon],
    [MonitoringPlanType.RestoreFiles, this.defaultIcon],
    [MonitoringPlanType.VMBackup, 'ico ico-vmware'],
    [MonitoringPlanType.VMRestore, 'ico ico-vmware'],
    [MonitoringPlanType.SQLBackup, 'ico ico-databased'],
    [MonitoringPlanType.SQLResore, 'ico ico-databased'],
    [MonitoringPlanType.ExchangeBackup, 'ico ico-exchange'],
    [MonitoringPlanType.ExchangeRestore, 'ico ico-exchange'],
    [MonitoringPlanType.BMSSBackup, 'ico ico-imagebased'],
    [MonitoringPlanType.BMSSRestore, 'ico ico-imagebased'],
    [MonitoringPlanType.ConsistencyCheck, 'ico ico-consistencycheck'],
    [MonitoringPlanType.EC2Backup, this.defaultIcon],
    [MonitoringPlanType.EC2Restore, this.defaultIcon],
    [MonitoringPlanType.HyperVBackup, 'ico ico-hyperv'],
    [MonitoringPlanType.HyperVRestore, 'ico ico-hyperv'],
    [MonitoringPlanType.ProxmoxBackup, 'ico ico-proxmox'],
    [MonitoringPlanType.ProxmoxRestore, 'ico ico-proxmox']
  ]);

  private static readonly monitoringPlanTypeTextMap: Map<MonitoringPlanType, string> = new Map([
    [MonitoringPlanType.NA, ''],
    [MonitoringPlanType.Backup, 'backup-history:planType.filesBackup'],
    [MonitoringPlanType.Restore, 'backup-history:planType.filesRestore'],
    [MonitoringPlanType.BackupFiles, 'backup-history:planType.filesBackup'],
    [MonitoringPlanType.RestoreFiles, 'backup-history:planType.filesRestore'],
    [MonitoringPlanType.VMBackup, 'backup-history:planType.vmwareBackup'],
    [MonitoringPlanType.VMRestore, 'backup-history:planType.vmwareRestore'],
    [MonitoringPlanType.SQLBackup, 'backup-history:planType.sqlBackup'],
    [MonitoringPlanType.SQLResore, 'backup-history:planType.sqlRestore'],
    [MonitoringPlanType.ExchangeBackup, 'backup-history:planType.exchangeBackup'],
    [MonitoringPlanType.ExchangeRestore, 'backup-history:planType.exchangeRestore'],
    [MonitoringPlanType.BMSSBackup, 'backup-history:planType.imageBasedBackup'],
    [MonitoringPlanType.BMSSRestore, 'backup-history:planType.imageBasedRestore'],
    [MonitoringPlanType.ConsistencyCheck, 'backup-history:planType.consistencyCheck'],
    [MonitoringPlanType.EC2Backup, 'backup-history:planType.ec2Backup'],
    [MonitoringPlanType.EC2Restore, 'backup-history:planType.ec2Restore'],
    [MonitoringPlanType.HyperVBackup, 'backup-history:planType.hypervBackup'],
    [MonitoringPlanType.HyperVRestore, 'backup-history:planType.hypervRestore']
  ]);

  public static getIcon(type: MonitoringPlanType): string {
    return isNumber(type) && this.monitoringPlanTypeIconMap.has(type) ? this.monitoringPlanTypeIconMap.get(type) : '';
  }

  public static getText(type: MonitoringPlanType, i18N: I18NextPipe): string {
    return isNumber(type) && this.monitoringPlanTypeTextMap.has(type) ? i18N.transform(this.monitoringPlanTypeTextMap.get(type)) : '';
  }

  public static getData(type: MonitoringPlanType, i18N: I18NextPipe): { icon: string; text: string } {
    return {
      icon: this.getIcon(type),
      text: this.getText(type, i18N)
    };
  }

  public static getTableIcon(status: BackupStatuses): string {
    switch (status) {
      case BackupStatuses.Running:
        return 'ico ico-Refresh';
      case BackupStatuses.Success:
        return 'ico ico-success';
      case BackupStatuses.Failed:
      case BackupStatuses.UnexpectedlyClosed:
      case BackupStatuses.MissingRun:
        return 'ico ico-failed';
      case BackupStatuses.Interrupted:
      case BackupStatuses.Warning:
        return 'ico ico-error';
      case BackupStatuses.Unknown:
      default:
        return '';
    }
  }

  public static GetStatusName(status: number): string {
    switch (status) {
      case BackupStatuses.Running:
      case BackupStatuses.Success:
      case BackupStatuses.Failed:
      case BackupStatuses.Interrupted:
      case BackupStatuses.Warning:
      case BackupStatuses.Scheduled:
        return BackupStatuses[status];
      case BackupStatuses.None:
        return '';
      case BackupStatuses.PlanCreated:
      case BackupStatuses.PlanDeleted:
      case BackupStatuses.MissingRun:
      case BackupStatuses.UnexpectedlyClosed:
        return LastPlanStatusDisplay[status];
      case BackupStatuses.Unknown:
        return 'Not started';
      default:
        return 'Unknown';
    }
  }
}
